import { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import {
    useDataTable,
    useDataTableExpansion,
    type DataTableButtonAction,
    type DataTableFilter
} from '@odo/shared-ui';
import {
    useCommunications,
    useDeleteCommunication,
    useInfiniteCommunications
} from '../../../../hooks/useCommunications';
import { useViewportTableLimit } from '../../../../hooks/useViewportTableLimit';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import type { Communication, CommunicationStatus } from '../../../../services/communications';
import { CategoryFilterDropdown } from './CategoryFilterDropdown';
import { COMMUNICATION_ROW_HEIGHT, CommunicationsTable } from './CommunicationsTable';
import type { CommunicationsSortBy } from './communicationColumns';
import { DeleteCommunicationConfirmModal } from './DeleteCommunicationConfirmModal';

const SEARCH_DEBOUNCE_MS = 300;
// Starting limit only: the paged fetch waits for the viewport measurement.
const VIEWPORT_DEFAULT_PAGE_SIZE = 20;
const STATUS_OPTIONS = [
    { value: 'open', label: 'Open' },
    { value: 'resolved', label: 'Resolved' }
];

// A type alias, not an interface: only aliases pick up TypeScript's implicit
// index signature, which is what lets this satisfy `DataTableFilterMap`.
type CommunicationFilters = { status: string[]; category: string | null };

interface CommunicationsTabProps {
    practiceId: string;
}

// Mirrors PatientsTab: the parent only mounts this while Communications is
// active (see PracticeDetail.tsx), so every hook here fetches unconditionally.
export function CommunicationsTab({ practiceId }: CommunicationsTabProps) {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const table = useDataTable<CommunicationsSortBy, CommunicationFilters>({
        defaultSort: { by: 'lastActivityAt', order: 'desc' },
        defaultLimit: VIEWPORT_DEFAULT_PAGE_SIZE,
        defaultFilters: { status: [], category: null }
    });
    const { status: statusFilter, category: categoryFilter } = table.filters;
    const isInfinite = table.mode === 'infinite';
    const expansion = useDataTableExpansion();

    const [search, setSearch] = useState('');
    const [deletingCommunication, setDeletingCommunication] = useState<Communication | null>(null);

    const { setSearch: commitSearch } = table;
    useEffect(() => {
        const handle = setTimeout(() => commitSearch(search.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [search, commitSearch]);

    const {
        isReady: pageSizeReady,
        tableCardRef,
        tableHeaderRef,
        tableBodyRef,
        paginationRef,
        remeasure
    } = useViewportTableLimit(table, { rowHeight: COMMUNICATION_ROW_HEIGHT });

    // Expanding moves the card between the tab pane and a full-screen overlay -
    // re-measure so a paged table's page size reflects the new height.
    useLayoutEffect(() => {
        remeasure();
    }, [expansion.expanded, remeasure]);

    const { page, limit, sort, order } = table.params;
    const sharedParams = {
        practice: practiceId,
        status: statusFilter[0] as CommunicationStatus | undefined,
        category: categoryFilter ?? undefined,
        search: table.search || undefined,
        sort,
        order
    };
    const paged = useCommunications({
        ...sharedParams,
        page,
        limit,
        enabled: !isInfinite && pageSizeReady
    });
    const infinite = useInfiniteCommunications({ ...sharedParams, limit, enabled: isInfinite });

    const communications = isInfinite ? infinite.communications : paged.communications;
    const meta = isInfinite ? infinite.meta : paged.meta;
    const isLoading = isInfinite ? infinite.isLoading : paged.isLoading;
    const isError = isInfinite ? infinite.isError : paged.isError;
    const error = isInfinite ? infinite.error : paged.error;
    const { refresh } = paged;

    useEffect(() => {
        if (isError) showToast(apiErrorMessage(error, 'Failed to load communications'), 'error');
    }, [isError, error, showToast]);

    const deleteCommunication = useDeleteCommunication();

    const totalPages = Math.max(1, Math.ceil(meta.total / limit));
    const hasFilters = statusFilter.length > 0 || categoryFilter !== null;
    const clearFilters = () => {
        setSearch('');
        table.setFilter('status', []);
        table.setFilter('category', null);
    };

    const goToNew = () => navigate(`/accounts/practices/${practiceId}/communications/new`);
    const goToCommunication = (communication: Communication) =>
        navigate(`/accounts/practices/${practiceId}/communications/${communication.id}`);

    const filters: DataTableFilter[] = [
        {
            key: 'status',
            label: 'Status',
            options: STATUS_OPTIONS,
            value: statusFilter[0] ?? null,
            onChange: (value) => table.setFilter('status', value ? [value] : []),
            align: 'center'
        }
    ];

    // "Log a communication" ahead of the expand toggle - CommunicationsTable
    // appends the toggle itself, after this array.
    const toolbarActions: DataTableButtonAction[] = [
        {
            key: 'log-communication',
            label: 'Log a communication',
            icon: <ChatBubbleLeftRightIcon className="h-4 w-4" />,
            variant: 'primary',
            onClick: goToNew
        }
    ];

    const handleConfirmDelete = () => {
        if (!deletingCommunication) return;
        deleteCommunication.mutate(
            { id: deletingCommunication.id, practiceId: deletingCommunication.practiceId },
            {
                onSuccess: () => {
                    showToast('Communication deleted', 'success');
                    setDeletingCommunication(null);
                },
                onError: (err) =>
                    showToast(apiErrorMessage(err, 'Failed to delete communication'), 'error')
            }
        );
    };

    return (
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-4">
            <CommunicationsTable
                communications={communications}
                isLoading={isLoading}
                isError={isError}
                search={table.search}
                hasFilters={hasFilters}
                onClearFilters={clearFilters}
                onRetry={() => void refresh()}
                onLogCommunication={goToNew}
                onOpenCommunication={goToCommunication}
                onDelete={setDeletingCommunication}
                mode={table.mode}
                onModeChange={table.setMode}
                sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
                pagination={{ page, totalPages, total: meta.total, onPage: table.setPage }}
                onLoadMore={infinite.fetchNextPage}
                hasMore={infinite.hasNextPage}
                isLoadingMore={infinite.isFetchingNextPage}
                tableCardRef={tableCardRef}
                tableHeaderRef={tableHeaderRef}
                tableBodyRef={tableBodyRef}
                paginationRef={paginationRef}
                expansion={expansion}
                searchConfig={{
                    value: search,
                    onChange: setSearch,
                    placeholder: 'Search communications'
                }}
                filters={filters}
                toolbarSlot={
                    <CategoryFilterDropdown
                        value={categoryFilter}
                        onChange={(value) => table.setFilter('category', value)}
                    />
                }
                toolbarActions={toolbarActions}
            />

            {deletingCommunication && (
                <DeleteCommunicationConfirmModal
                    communication={deletingCommunication}
                    isDeleting={deleteCommunication.isPending}
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setDeletingCommunication(null)}
                />
            )}
        </div>
    );
}
